// BHL cart-level cross-sell (2026-10-14): fills the "You might also need" strip rendered empty and hidden by
// snippets/bhl-cart-cross-sell.liquid (drawer; basket page wide placement in cart-products, narrow placement in
// sections/main-cart.liquid after the summary). Each suggestion is one link to its product page (Nathan, P172: no
// quick add). The strip never changes the basket: no add control, no cart request, no cart event.
//
// Structure (so the behaviour can be tested without a browser; tests/test_recommendations_2026_10_14.py runs it under
// node with fake fetch, clock and view; tests/test_recommendations_images_2026_10_14.py covers the 2026-10-14 image change):
//   pure helpers     parseIdList, pickProducts, planRender, nextFocusId, focusTarget, ResponseCache
//   StripController  refresh state for one strip: basket revision, generation, visibility, timers, cached answers,
//                    card plan and focus. Talks to the page only through a view adapter.
//   DomView          the view adapter on the real DOM (cards kept and refreshed in place, focus moves).
//   <bhl-cart-cross-sell>  glue: attributes, visibility (IntersectionObserver, drawer pinning, breakpoint), basket events.
//
// Loading: this module is loaded by snippets/cart-products.liquid on every basket render, empty baskets included.
// Nothing is requested while the strip is not visible. Answers are scoped to the basket revision; a failed request is
// not cached and is retried at most twice per revision, when the strip is visible again.
import { StandardEvents } from '@shopify/events';

export const MAX_ATTEMPTS = 3; // per revision and request: the first try and two retries
export const REFRESH_DELAY_MS = 150;
/** 2026-10-14 images: fewer qualifying suggestions than this and the strip shows none (never a single lonely card). */
export const MIN_ITEMS = 2;

// ------------------------------------------------------------------ pure helpers

/** @param {string | undefined | null} value */
export function parseIdList(value) {
  return String(value || '')
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);
}

/**
 * Round robin across the answers (most recently added basket product first), so each anchor contributes; products in
 * the basket, unavailable products and repeats are left out; at most `limit`.
 * @template {{ productId: string, available: boolean }} T
 * @param {T[][]} lists
 * @param {Iterable<string>} inCart
 * @param {number} limit
 * @returns {T[]}
 */
export function pickProducts(lists, inCart, limit) {
  const exclude = new Set(inCart);
  const seen = new Set();
  /** @type {T[]} */
  const picked = [];
  const longest = Math.max(0, ...lists.map((list) => list.length));
  for (let i = 0; i < longest && picked.length < limit; i++) {
    for (const list of lists) {
      if (picked.length >= limit) break;
      const item = list[i];
      if (!item || !item.productId || !item.available) continue;
      if (exclude.has(item.productId) || seen.has(item.productId)) continue;
      seen.add(item.productId);
      picked.push(item);
    }
  }
  return picked;
}

/**
 * Which cards to keep, add and remove. Kept cards stay in their current order and are never moved (moving a node
 * loses focus); new cards are appended after them.
 * @param {string[]} currentIds
 * @param {string[]} pickedIds
 * @param {number} limit
 */
export function planRender(currentIds, pickedIds, limit) {
  const wanted = new Set(pickedIds);
  const keep = currentIds.filter((id) => wanted.has(id)).slice(0, limit);
  const kept = new Set(keep);
  const add = pickedIds.filter((id) => !kept.has(id)).slice(0, Math.max(0, limit - keep.length));
  const remove = currentIds.filter((id) => !kept.has(id));
  return { keep, add, remove, order: [...keep, ...add] };
}

/**
 * The next surviving card after the focused one, else the previous one, else null.
 * @param {string[]} currentIds
 * @param {string} focusedId
 * @param {Set<string>} removed
 */
export function nextFocusId(currentIds, focusedId, removed) {
  const at = currentIds.indexOf(focusedId);
  if (at === -1) return null;
  for (let i = at + 1; i < currentIds.length; i++) if (!removed.has(currentIds[i])) return currentIds[i];
  for (let i = at - 1; i >= 0; i--) if (!removed.has(currentIds[i])) return currentIds[i];
  return null;
}

/**
 * Where focus goes when the focused card is removed: a surviving card, else the heading while cards remain (new ones),
 * else the basket lines. null when the focused card survives (or nothing in the strip had focus).
 * @param {string[]} currentIds
 * @param {string | null} focusedId
 * @param {Set<string>} removed
 * @param {number} cardsAfter
 * @returns {{ kind: 'card', id: string } | { kind: 'heading' } | { kind: 'lines' } | null}
 */
export function focusTarget(currentIds, focusedId, removed, cardsAfter) {
  if (!focusedId || !removed.has(focusedId)) return null;
  const id = nextFocusId(currentIds, focusedId, removed);
  if (id) return { kind: 'card', id };
  return cardsAfter > 0 ? { kind: 'heading' } : { kind: 'lines' };
}

/** Answers per basket revision and URL. Only completed answers are kept; failures are evicted and counted. */
export class ResponseCache {
  /** @type {Map<string, Promise<any>>} */
  #entries = new Map();
  /** @type {Map<string, number>} */
  #failures = new Map();
  #revision = '';

  /** Drop everything that belongs to another basket revision. @param {string} revision */
  scope(revision) {
    if (revision === this.#revision) return;
    this.#revision = revision;
    this.#entries.clear();
    this.#failures.clear();
  }

  /** @param {string} url */
  get(url) {
    return this.#entries.get(url);
  }

  /** @param {string} url */
  canTry(url) {
    return (this.#failures.get(url) || 0) < MAX_ATTEMPTS;
  }

  /** @param {string} url @param {Promise<any>} promise */
  set(url, promise) {
    this.#entries.set(url, promise);
  }

  /** @param {string} url @param {string} revision */
  fail(url, revision) {
    if (revision !== this.#revision) return;
    this.#entries.delete(url);
    this.#failures.set(url, (this.#failures.get(url) || 0) + 1);
  }

  get size() {
    return this.#entries.size;
  }
}

// ------------------------------------------------------------------ one strip's refresh state

/**
 * @typedef {object} StripView
 * @property {() => string[]} cardIds
 * @property {() => string | null} focusedCardId
 * @property {(ids: string[]) => void} remove
 * @property {(id: string, item: any) => void} update  refresh a surviving card in place (link, image, title, price)
 * @property {(items: any[]) => void} append
 * @property {(hidden: boolean) => void} setHidden
 * @property {(target: { kind: string, id?: string }) => void} focus
 *
 * @typedef {object} Clock
 * @property {(fn: () => void, ms: number) => any} setTimeout
 * @property {(handle: any) => void} clearTimeout
 * @property {(fn: () => void) => any} idle
 * @property {(handle: any) => void} cancelIdle
 */
export class StripController {
  /**
   * @param {{ view: StripView, fetchItems: (url: string) => Promise<any[]>, urlFor: (productId: string) => string,
   *   clock: Clock, cache: ResponseCache, limit: number, anchors: number, revision: string, inCart: string[] }} options
   */
  constructor(options) {
    this.o = options;
    this.revision = options.revision;
    this.inCart = options.inCart;
    this.generation = 0;
    this.dirty = true;
    this.visible = false;
    this.connected = true;
    this.timer = null;
    this.idleHandle = null;
  }

  /** New basket attributes (revision and products). */
  setBasket(/** @type {string} */ revision, /** @type {string[]} */ inCart) {
    if (!this.connected) return;
    if (revision === this.revision && inCart.join(',') === this.inCart.join(',')) return;
    this.revision = revision;
    this.inCart = inCart;
    this.generation++;
    this.dirty = true;
    // a card whose product is now in the basket goes at once
    this.#apply(new Set(inCart.filter((id) => this.o.view.cardIds().includes(id))), [], []);
    this.schedule();
  }

  /** A basket update event settled (the attributes normally change as well). */
  settled() {
    if (!this.connected) return;
    this.generation++;
    this.dirty = true;
    this.schedule();
  }

  setVisible(/** @type {boolean} */ visible) {
    this.visible = visible;
    this.schedule();
  }

  disconnect() {
    this.connected = false;
    this.#cancel();
  }

  #cancel() {
    if (this.timer !== null) this.o.clock.clearTimeout(this.timer);
    if (this.idleHandle !== null) this.o.clock.cancelIdle(this.idleHandle);
    this.timer = this.idleHandle = null;
  }

  schedule() {
    if (!this.connected || !this.dirty || !this.visible) return; // deferred until visible
    this.#cancel();
    this.timer = this.o.clock.setTimeout(() => {
      this.timer = null;
      this.idleHandle = this.o.clock.idle(() => {
        this.idleHandle = null;
        this.load();
      });
    }, REFRESH_DELAY_MS);
  }

  async load() {
    if (!this.connected || !this.visible) return; // stays dirty; loads when visible again
    const generation = this.generation;
    const revision = this.revision;
    this.dirty = false;
    this.o.cache.scope(revision);
    const results = await Promise.all(this.inCart.slice(0, this.o.anchors).map((id) => this.#fetch(id, revision)));
    if (!this.connected || generation !== this.generation || revision !== this.revision) return; // stale answer
    const picked = pickProducts(
      results.map((r) => r.items),
      this.inCart,
      this.o.limit
    );
    const byId = new Map(picked.map((item) => [item.productId, item]));
    const plan = planRender(this.o.view.cardIds(), [...byId.keys()], this.o.limit);
    this.#apply(
      new Set(plan.remove),
      plan.keep.map((id) => [id, byId.get(id)]),
      plan.add.map((id) => byId.get(id))
    );
    if (results.some((r) => r.failed && r.retryable)) this.dirty = true; // retried when visible again
  }

  /** @param {string} productId @param {string} revision */
  #fetch(productId, revision) {
    const cache = this.o.cache;
    const url = this.o.urlFor(productId);
    const cached = cache.get(url);
    if (cached) return cached;
    if (!cache.canTry(url)) return Promise.resolve({ items: [], failed: true, retryable: false });
    const request = this.o
      .fetchItems(url)
      .then((items) => ({ items, failed: false, retryable: false }))
      .catch(() => {
        cache.fail(url, revision);
        return { items: [], failed: true, retryable: cache.canTry(url) };
      });
    cache.set(url, request);
    return request;
  }

  /**
   * @param {Set<string>} remove
   * @param {Array<[string, any]>} updates
   * @param {any[]} adds
   */
  #apply(remove, updates, adds) {
    const view = this.o.view;
    const currentIds = view.cardIds();
    const focused = view.focusedCardId();
    let after = currentIds.filter((id) => !remove.has(id)).length + adds.length;
    if (after > 0 && after < MIN_ITEMS) {
      // fewer than MIN_ITEMS would be left: show none (every card goes; focus then moves to the basket lines)
      remove = new Set(currentIds);
      updates = [];
      adds = [];
      after = 0;
    }
    const target = focusTarget(currentIds, focused, remove, after);
    if (remove.size) view.remove([...remove]);
    for (const [id, item] of updates) view.update(id, item);
    if (adds.length) view.append(adds);
    view.setHidden(view.cardIds().length === 0);
    if (target) view.focus(target);
  }
}

// ------------------------------------------------------------------ the DOM view

/** @implements {StripView} */
class DomView {
  /** @param {HTMLElement} host */
  constructor(host) {
    this.host = host;
  }

  get list() {
    return /** @type {HTMLElement | null} */ (this.host.querySelector('.bhl-xsell__list'));
  }

  /** @returns {HTMLElement[]} */
  cards() {
    const list = this.list;
    return list ? /** @type {HTMLElement[]} */ (Array.from(list.querySelectorAll(':scope > [data-product-id]'))) : [];
  }

  /** @param {string} id */
  card(id) {
    return this.cards().find((card) => card.dataset.productId === id);
  }

  cardIds() {
    return this.cards().map((card) => card.dataset.productId || '');
  }

  focusedCardId() {
    const active = document.activeElement;
    const card = active ? this.cards().find((c) => c.contains(active)) : undefined;
    return card ? card.dataset.productId || null : null;
  }

  /** @param {string[]} ids */
  remove(ids) {
    for (const id of ids) this.card(id)?.remove();
  }

  /**
   * Refresh a surviving card in place from the newer answer. The card's one link (the focusable element) is kept, so
   * focus stays on it; only its address and its non-focusable content (image, title, price) are replaced.
   * @param {string} id @param {{ node: Element }} item
   */
  update(id, item) {
    const card = this.card(id);
    const link = card?.querySelector('.bhl-xsell__link');
    const freshLink = item?.node.querySelector('.bhl-xsell__link');
    if (!card || !link || !freshLink) return;
    const available = item.node.getAttribute('data-available');
    if (available !== null) card.setAttribute('data-available', available);
    const href = freshLink.getAttribute('href');
    if (href && href !== link.getAttribute('href')) link.setAttribute('href', href);
    const fresh = freshLink.childNodes;
    const unchanged = link.childNodes.length === fresh.length && Array.from(link.childNodes).every((node, i) => node.isEqualNode(fresh[i]));
    if (!unchanged) {
      link.replaceChildren(...Array.from(freshLink.childNodes, (node) => document.importNode(node, true)));
    }
  }

  /** @param {Array<{ node: Element }>} items */
  append(items) {
    const list = this.list;
    if (!list) return;
    for (const item of items) if (item) list.append(document.importNode(item.node, true));
  }

  /** @param {boolean} hidden */
  setHidden(hidden) {
    const inner = /** @type {HTMLElement | null} */ (this.host.querySelector('.bhl-xsell__inner'));
    if (inner) inner.hidden = hidden;
  }

  /** @param {{ kind: string, id?: string }} target */
  focus(target) {
    /** @type {HTMLElement | null | undefined} */
    let el = null;
    if (target.kind === 'card' && target.id) el = this.card(target.id)?.querySelector('.bhl-xsell__link');
    if (!el && target.kind !== 'lines') el = this.host.querySelector('.bhl-xsell__heading');
    if (!el) el = (this.host.closest('cart-items-component') || document).querySelector('.cart-items__title');
    /** @type {HTMLElement | null | undefined} */ (el)?.focus();
  }
}

// ------------------------------------------------------------------ page wiring

const responseCache = new ResponseCache();

/** @type {Clock} */
const browserClock = {
  setTimeout: (fn, ms) => window.setTimeout(fn, ms),
  clearTimeout: (handle) => window.clearTimeout(handle),
  idle: (fn) => ('requestIdleCallback' in window ? window.requestIdleCallback(fn, { timeout: 1500 }) : window.setTimeout(fn, 0)),
  cancelIdle: (handle) => ('cancelIdleCallback' in window ? window.cancelIdleCallback(handle) : window.clearTimeout(handle)),
};

class BhlCartCrossSell extends HTMLElement {
  static observedAttributes = ['data-cart-revision', 'data-cart-product-ids'];

  /** @type {StripController | null} */
  #controller = null;
  /** @type {AbortController | null} */
  #listeners = null;
  /** @type {IntersectionObserver | null} */
  #observer = null;
  /** @type {MutationObserver | null} */
  #dialogObserver = null;
  #intersecting = false;

  connectedCallback() {
    this.#controller = new StripController({
      view: new DomView(this),
      fetchItems: async (url) => {
        const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store' });
        if (!response.ok) throw new Error(`Recommendations ${response.status}`);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        return Array.from(doc.querySelectorAll('[data-bhl-xsell-item]')).map((node) => ({
          productId: node.getAttribute('data-product-id') || '',
          available: node.getAttribute('data-available') === 'true',
          node,
        }));
      },
      urlFor: (productId) =>
        `${this.dataset.url}?product_id=${encodeURIComponent(productId)}` +
        `&intent=complementary&limit=10&section_id=${encodeURIComponent(this.dataset.section || '')}`,
      clock: browserClock,
      cache: responseCache,
      limit: Number(this.dataset.limit) || 4,
      anchors: Number(this.dataset.anchors) || 3,
      revision: this.dataset.cartRevision || '',
      inCart: parseIdList(this.dataset.cartProductIds),
    });
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;
    // read only: a settled basket change (from anywhere on the page) is a reason to refresh
    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      (event) => {
        const promise = /** @type {any} */ (event).promise;
        const settle = () => this.#controller?.settled();
        promise?.then(settle, settle);
      },
      { signal }
    );
    // breakpoint changes switch the page instances between display none and shown
    window.matchMedia('(min-width: 750px)').addEventListener('change', () => this.#updateVisibility(), { signal });
    this.#observe();
  }

  disconnectedCallback() {
    this.#listeners?.abort();
    this.#listeners = null;
    this.#observer?.disconnect();
    this.#observer = null;
    this.#dialogObserver?.disconnect();
    this.#dialogObserver = null;
    this.#controller?.disconnect();
    this.#controller = null;
  }

  attributeChangedCallback(_name, oldValue, newValue) {
    if (oldValue === newValue) return;
    this.#controller?.setBasket(this.dataset.cartRevision || '', parseIdList(this.dataset.cartProductIds));
  }

  #updateVisibility() {
    // rendered at this width and drawer mode (display none has no boxes), and on screen or near it
    this.#controller?.setVisible(this.#intersecting && this.getClientRects().length > 0);
  }

  #observe() {
    if (!('IntersectionObserver' in window)) {
      this.#intersecting = true;
      this.#updateVisibility();
      return;
    }
    const drawer = this.dataset.context === 'drawer' ? this.closest('dialog') : null;
    let root = null;
    let rootMargin = '0px';
    if (!drawer) {
      // Horizon scrolls .page-wrapper from 990 px (see product-recommendations.js); a viewport root would clip the margin.
      const wrapper = document.querySelector('.page-wrapper');
      root = wrapper && getComputedStyle(wrapper).overflowY !== 'visible' ? wrapper : null;
      rootMargin = '0px 0px 300px 0px';
    }
    this.#observer = new IntersectionObserver(
      (entries) => {
        this.#intersecting = entries[entries.length - 1]?.isIntersecting ?? false;
        this.#updateVisibility();
      },
      { root, rootMargin }
    );
    this.#observer.observe(drawer || this);
    if (drawer) {
      // the drawer shows or hides the strip when it pins or unpins its summary, and when it opens or closes
      this.#dialogObserver = new MutationObserver(() => this.#updateVisibility());
      this.#dialogObserver.observe(drawer, { attributes: true, attributeFilter: ['cart-summary-sticky', 'open'] });
    }
  }
}

if (!customElements.get('bhl-cart-cross-sell')) {
  customElements.define('bhl-cart-cross-sell', BhlCartCrossSell);
}
